import React from "react";
import { AvatarWithInitials } from "@/shared/ui/Avatar";
import { Column, Row } from "@/shared/ui/Blocks";
import { Icon, IconName } from "@/shared/ui/Icon";
import { IconButton } from "@/shared/ui/IconButton";
import { SubtitleText } from "@/shared/ui/Typography";
import { Card } from "@/shared/ui/Card";
import { DropdownMenu } from "@/shared/widgets/DropdownMenu";
import useOrgNavigate from "@/shared/utils/router/useOrgNavigate";
import { upperCase } from "lodash";
import Chip from "@/shared/ui/Chip";
import { FeatureGate } from "@/shared/widgets";
import { FeatureFlagKey } from "@/types/feature-flags";
import { getIsSurveysCompleted } from "@/shared/utils/selectors/getSurveyCompletion";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";

interface ActionItem {
  label: string;
  onClick: () => void;
  icon: IconName;
  disabled?: boolean;
}

interface LabelItem {
  title: string;
  color: any;
}

interface PositionKanbanCardProps {
  item: any;
  projectId: number;
  actions?: ActionItem[];
  labels?: LabelItem[];
  dimensionConfig?: AssessmentSurveyType[];
}

function PositionKanbanCard(props: PositionKanbanCardProps) {
  const { item = {}, projectId, actions, labels, dimensionConfig } = props;

  const navigate = useOrgNavigate();

  const candidateProfilePath = `projects/${projectId}/person/${item.id}`;

  const profileComplete = getIsSurveysCompleted(
    item?.completed_dim_types as AssessmentSurveyType[],
    dimensionConfig
  );

  const cardLabels = [
    profileComplete && {
      title: "Profile Complete",
      color: "blue",
    },
    ...(labels || []),
    item?.invite && {
      title: "Invited",
      color: "orange",
    },
    item?.is_contact && {
      title: "Contact",
      color: "grey",
    },
    !item?.is_contact &&
      !item?.invite &&
      !profileComplete && {
        title: "Profile Incomplete",
        color: "orange",
      },
  ].filter(Boolean);

  const hasActions = actions?.length && actions?.length > 0;

  return (
    <Card
      className="w-full py-2 group"
      onClick={() => navigate(candidateProfilePath)}
    >
      <Row className="justify-between items-center px-2">
        <Row className="space-x-2 items-center">
          <div className="relative w-8 h-8 flex items-center justify-center">
            <Icon
              className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              icon="Drag"
              color="lightGrey"
              size="sm"
            />
            <AvatarWithInitials
              size="sm"
              className="absolute opacity-100 group-hover:opacity-0 transition-opacity duration-300"
              avatar={item?.avatar}
              initials={item?.initials}
              isContact={item?.is_contact}
            />
          </div>
          <Column className="max-w-full">
            <p className="font-semibold">{item?.name}</p>
            <SubtitleText className="max-w-[200px] truncate">
              {item?.email}
            </SubtitleText>
          </Column>
        </Row>
        {hasActions && (
          <Row>
            <DropdownMenu
              items={actions}
              separatorIndexes={[3, actions.length - 1]}
              trigger={
                <IconButton
                  icon="KebabVertical"
                  color="black"
                  variant="ghost"
                />
              }
              side="bottom"
              align="end"
            />
          </Row>
        )}
      </Row>
      <FeatureGate flagKey={FeatureFlagKey.PROJECT_KANBAN_LABELS}>
        {cardLabels?.length > 0 && (
          <Row className="items-center gap-2 px-2 py-1 pl-10">
            {cardLabels.map((label, index) => (
              <Chip
                key={index}
                color={label.color}
                variant="contrast"
                className="px-2 py-1 text-2xs leading-none tracking-[1px]"
              >
                {upperCase(label.title)}
              </Chip>
            ))}
          </Row>
        )}
      </FeatureGate>
    </Card>
  );
}

export default PositionKanbanCard;
